import * as React from "react";
import { Card } from "antd";
import { nameOfDomainStatus, tagLinkUrlPrefixes } from "../../services/utils";
import { TagLinksTable } from "../../components/detail/tagLinksTable";
import { Domain, DomainStatusEndpoint, DomainStatusLocation } from "../../schema/types/domain/entity";
import { CardContent } from "../../components/antd/card/CardContent";
import { FormItem } from "../../components/antd/form/FormItem";
import { FormLabel } from "../../components/antd/form/FormLabel";
import { FormItemValue } from "../../components/antd/form/FormItemValue";
import { Table } from "../../newcomponents/table/table";
import { ExternalLink } from "../../newcomponents/table/components/ExternalLink";
import { InternalLink } from "../../newcomponents/table/components/InternalLink";
import { parseLink } from "../../utils/linkParser/linkParser";

function getStatusColorClass(status?: string): string {
  switch (status) {
    case "ready":
      return "color-action";
    case "warning":
    case "pendingDnsConfig":
    case "pendingCertificate":
      return "color-warning";
    case "errored":
    case "deleted":
      return "color-danger";
    default:
      return "";
  }
}

interface Props {
  domain: Domain;
}
export const Summary: React.FC<Props> = ({ domain }) => {
  const hasTagLinks =
    Object.entries(domain.tags || {}).filter(([_, value]) =>
      tagLinkUrlPrefixes.some((p) => String(value).startsWith(p)),
    ).length > 0;

  let { endpoints, locations }: { endpoints: DomainStatusEndpoint[]; locations: DomainStatusLocation[] } =
    domain.status || ({} as any);

  if (!endpoints) {
    endpoints = [];
  }
  if (!locations) {
    locations = [];
  }

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container" data-section-id={"summary"}>
        <Card id={"summary"}>
          <CardContent>
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormItemValue>{domain.description || domain.name}</FormItemValue>
            </FormItem>
            {domain.status?.status ? (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <FormItemValue className={getStatusColorClass(domain.status.status)}>
                  {nameOfDomainStatus(domain.status)}
                </FormItemValue>
              </FormItem>
            ) : null}
            {domain.status?.warning ? (
              <FormItem>
                <FormLabel>Warning</FormLabel>
                <FormItemValue className="color-warning">{domain.status.warning}</FormItemValue>
              </FormItem>
            ) : null}
            {hasTagLinks ? (
              <TagLinksTable
                tableId="domain-info-tag-links"
                classNames={{ container: `mt-12` }}
                tags={Object.entries(domain.tags || {})
                  .filter(([_, value]) => tagLinkUrlPrefixes.some((p) => String(value).startsWith(p)))
                  .map(([key, value]) => ({ key, value }))}
                styles={{ container: { width: "100%" }, header: { marginBottom: 0 } }}
              />
            ) : null}
            <div className="mt-8">
              <Table
                tableId={`domain-summary-endpointsa`}
                data={endpoints}
                title={() => (
                  <div>
                    <FormLabel>Endpoints</FormLabel>
                  </div>
                )}
                styles={{ header: { marginBottom: 0 } }}
                hideSettings
                columns={[
                  {
                    id: "url",
                    label: "URL",
                    cell: (props) => {
                      const value = props.getValue();

                      return <ExternalLink label={value} url={value} />;
                    },
                  },
                  {
                    id: "workloadLink",
                    label: "Workload",
                    cell: (props) => {
                      const value = props.getValue();
                      const label = parseLink(value, { useInputCtx: true }).name;

                      return <InternalLink label={label} to={`/console${value}`} />;
                    },
                  },
                ]}
              />
            </div>
            <div className="mt-8">
              <Table
                tableId={`domain-summary-locationsa`}
                data={locations}
                title={() => (
                  <div>
                    <FormLabel>Locations</FormLabel>
                  </div>
                )}
                styles={{ header: { marginBottom: 0 } }}
                hideSettings
                columns={[
                  { id: "name", label: "Name" },
                  {
                    id: "certificateStatus",
                    label: "Certificate Status",
                    cell: ({ getValue }) => {
                      const value = getValue() as string;
                      return <span className={getStatusColorClass(value)}>{value || "-"}</span>;
                    },
                  },
                ]}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
